import React, { useMemo } from "react";
import moment from "moment";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  StyledDate,
  StyledChevronRightHover,
  LabelLink,
  StyledNeedsReview,
  ReportComponentWrapper,
  ComponentInfoWrapper,
  ContentWrapper,
  ReportName,
  ReportNameSection
} from "./styles";

const handleShouldNotCalculate = (calculationType, cType, shouldCalculateCondition) => cType === calculationType && shouldCalculateCondition;
const ReportComponent = ({ selectedReport, component, fetchedComponents, redirect, dealId, reportId }) => {
  const { shouldCalculateDebtYield, shouldCalculateDscr } = selectedReport;
  const { type, name, calcContext, redirectTypePath } = component;

  const reportComponentByType = fetchedComponents && fetchedComponents.find(rep => rep.type === type);

  const reportComponentHasLineItems = useMemo(() => {
    if (!reportComponentByType) {
      return false;
    }

    if (reportComponentByType.type === "NOI") {
      return reportComponentByType.lineItems && reportComponentByType.lineItems.length > 0;
    } else if (reportComponentByType.type === "DSCR" || reportComponentByType.type === "DebtYield") {
      return (
        reportComponentByType.calculations &&
        reportComponentByType.calculations.selectedLoans &&
        reportComponentByType.calculations.selectedLoans.some((loan) => loan.lineItem)
      );
    } else {
      return false;
    }
  }, [reportComponentByType]);

  if (handleShouldNotCalculate("DebtYield", type, !shouldCalculateDebtYield)
      || handleShouldNotCalculate("DSCR", type, !shouldCalculateDscr)
      || handleShouldNotCalculate("NOI", type, (!shouldCalculateDebtYield && !shouldCalculateDscr))
  ) {
    return null;
  }

  const handleClick = () => {
    const reportComponentId = reportComponentByType ? reportComponentByType.id : "new";
    let path;
    switch (`${type}-${calcContext}`) {
      case "DebtYield-regular":
        path = `/deal/${dealId}/lender-reports-debt-yield-config/${reportId}`;
        break;
      case "DSCR-regular":
        path = `/deal/${dealId}/lender-reports-dscr-config/${reportId}`;
        break;
      case "NOI-regular":
        path = `/deal/${dealId}/lender-reports/${reportId}/${redirectTypePath}/${reportComponentId}`;
        break;
        case "DebtYield-projected":
          path = `/deal/${dealId}/lender-reports-projected-debt-yield-config/${reportId}`;
          break;
        case "DSCR-projected":
          path = `/deal/${dealId}/lender-reports-projected-dscr-config/${reportId}`;
          break;
        case "NOI-projected":
          path = `/deal/${dealId}/lender-reports/${reportId}/${redirectTypePath}/${reportComponentId}`;
          break;
      default:
        path = `/deal/${dealId}/lender-reports`;
    }

    redirect(path);
  };

  const getStyledDate = () => {
    let lastUpdated;
    switch (`${type}-${calcContext}`) {
      case "NOI-regular":
        lastUpdated = selectedReport.noiLastUpdatedDate;
        break;
      case "DebtYield-regular":
        lastUpdated = selectedReport.debtYieldLastUpdatedDate;
        break;
      case "DSCR-regular":
        lastUpdated = selectedReport.dscrLastUpdatedDate;
        break;
        case "NOI-projected":
          lastUpdated = selectedReport.noiProjectedLastUpdatedDate;
          break;
        case "DebtYield-projected":
          lastUpdated = selectedReport.debtYieldProjectedLastUpdatedDate;
          break;
        case "DSCR-projected":
          lastUpdated = selectedReport.dscrProjectedLastUpdatedDate;
          break;
      default:
        lastUpdated = null;
    }

    return lastUpdated && <StyledDate>{`Configuration last updated on ${moment.utc(lastUpdated).format("MMMM DD, YYYY")}`}</StyledDate>;
  };

  return (
    <ReportComponentWrapper>
      <ComponentInfoWrapper>
        <LabelLink onClick={handleClick}>{name}</LabelLink>
        {reportComponentHasLineItems ? getStyledDate() : <StyledNeedsReview> NEEDS REVIEW</StyledNeedsReview>}
      </ComponentInfoWrapper>
      <StyledChevronRightHover onClick={handleClick} />
    </ReportComponentWrapper>
  );
};

const HeroCardContent = props => {
  const { selectedReportGroup, reportComponentsForSelectedGroup,reportProjectedComponentsForSelectedGroup, dealId, reportId, history, isGuest } = props;
  const { bar840FrontEndIsProjectedComponentsFeature } = useFlags();
  const reportComponents = [
    {
      type: "NOI",
      name: "Adjusted Net Operating Income",
      calcContext: "regular",
      redirectTypePath: "noi-calculation"
    },
    {
      type: "DSCR",
      name: "Debt Service for DSCR",
      calcContext: "regular",
      redirectTypePath: "dscr-calculation"
    },
    {
      type: "DebtYield",
      name: "Loan Balance for Debt Yield",
      calcContext: "regular",
      redirectTypePath: "debt-yield-calculation"
    }
  ];
  
  if(bar840FrontEndIsProjectedComponentsFeature){
    reportComponents.push(...[
    {
      type: "NOI",
      name: "Projected Adjusted Net Operating Income",
      calcContext: "projected",
      redirectTypePath: "projected-noi-calculation"
    },
    {
      type: "DSCR",
      name: "Projected Debt Service for DSCR",
      calcContext: "projected",
      redirectTypePath: "projected-dscr-calculation"
    },
    {
      type: "DebtYield",
      name: "Projected Loan Balance for Debt Yield",
      calcContext: "projected",
      redirectTypePath: "projected-debt-yield-calculation"
    }
  ]);
  }

  if (!selectedReportGroup) {
    return null;
  }

  const { firstReportDueDate, frequency, reportName } = selectedReportGroup;

  const getDateAndFrequency = () => {
    if (!firstReportDueDate || !frequency) {
      return "";
    }

    return `Started on ${moment.utc(firstReportDueDate).format("MMMM DD, YYYY")} and published ${frequency}`;
  };

  const redirectToReportComponent = path => {
    history.push(path, { from: "edit-report-hero-card" });
  };

  return (
    <ContentWrapper>
      <ReportNameSection>
        <ReportName>{reportName}</ReportName>
        <StyledDate>{getDateAndFrequency()}</StyledDate>
      </ReportNameSection>
      {!isGuest &&
        reportComponents.map(
          comp => selectedReportGroup && (
            <ReportComponent
              key={`${comp.type}-${comp.calcContext}`}
              selectedReport={selectedReportGroup}
              component={comp}
              fetchedComponents={comp.calcContext === "regular" ? reportComponentsForSelectedGroup : reportProjectedComponentsForSelectedGroup}
              redirect={redirectToReportComponent}
              dealId={dealId}
              reportId={reportId}
            />
          )
        )}
    </ContentWrapper>
  );
};

export default HeroCardContent;
